import Chip from "@web-ui/components/Chip";
import Link from "@web-ui/adapters/link";
import { usePathname } from "@web-ui/adapters/navigation";
import Icon from "@web-ui/components/Icon";

/*
 * One sidebar item. The active item is marked aria-current="page". Its pill: in the Sidebar's top level
 * the pill is one shared element that slides between items (Sidebar draws it, motion-rail; owner
 * 2026-10-13), so a top level item draws its own pill only until the rail has measured (`pill`), without
 * motion, which is what the server renders. A nested item (Sidebar/Dropdown) keeps its own pill and
 * reveals it. The template's mock routes (Customer list, Shop, Refunds) are gone.
 */
type NavLinkProps = {
    value: {
        href: string;
        title: string;
        icon?: string;
        counter?: number;
    };
    onClick?: () => void;
    /** Draw the active pill on this item (true, the default) or leave it to the Sidebar's rail (false). */
    pill?: boolean;
    /** The Sidebar's rail follows items marked this way. */
    railItem?: boolean;
};

const NavLink = ({ value, onClick, pill = true, railItem }: NavLinkProps) => {
    const pathname = usePathname();
    const isActive = pathname === value.href;

    return (
        <Link
            className={`group relative flex items-center shrink-0 gap-3 px-3 text-button transition-colors hover:text-t-primary ${
                value.icon ? "h-12" : "h-11"
            } ${isActive ? "text-t-primary" : "text-t-secondary"}`}
            href={value.href}
            key={value.title}
            onClick={onClick}
            aria-current={isActive ? "page" : undefined}
            data-rail-item={railItem || undefined}
        >
            {isActive && pill && (
                <div className={`absolute inset-0 gradient-menu rounded-full shadow-depth-menu ${railItem ? "" : "motion-reveal"}`} aria-hidden="true">
                    <div className="absolute inset-0.25 bg-b-pop rounded-full"></div>
                </div>
            )}
            {value.icon && (
                <Icon
                    className={`relative z-2 transition-colors group-hover:fill-t-primary ${
                        isActive ? "fill-t-primary" : "fill-t-secondary"
                    }`}
                    name={value.icon}
                />
            )}
            <div className="relative z-2 me-3">{value.title}</div>
            {value.counter ? (
                // Our Tixora tag (owner 2026-10-11): sizes to its number, so 100 never overflows a fixed square.
                <Chip tone="warning" size="sm" className="relative z-2 ms-auto tabular-nums">{value.counter}</Chip>
            ) : null}
        </Link>
    );
};

export default NavLink;
